/**
 * cypSnapEvidence.js — resolve an informant's SNAP-IV response for one DSM
 * symptom, for display beside the clinician's decision on the CYP SSI
 * symptom card.
 *
 * The adult SSI shows the patient's and informant's rating-scale response
 * next to each symptom (SSISymptomQuestionPanel's getInformantFormValue),
 * matched by the DSM code the scale item declares in
 * prefillsToProviderVarNames. The CYP SNAP-IV 26 declares exactly the same
 * mapping (python-functions/constants/cyp_adhd/scales/snap_iv_26.py tags
 * items 1-18 with A1a..A2i), so this is the same resolution against CYP's
 * storage: the informant's formInstances/snapIV26Informant document, whose
 * formDefinition was snapshotted at account creation and whose answers are
 * written by the live form runner.
 *
 * Pure functions, no Firestore — the SSI page already holds every
 * informant's form instances via ModularPatientDataContext.
 */

export const CYP_SNAP_FORM_KEY = 'snapIV26Informant';

/** Display name for the instrument, as on the adult evidence badge. */
export const CYP_SNAP_SCALE_NAME = 'SNAP-IV 26';

/** The shape every caller gets back for an item with no stored answer. */
const NO_ANSWER = Object.freeze({ value: null, freeText: null });

/**
 * Parse a single raw answer value into the { value, freeText } shape.
 *
 * Stored shapes, all produced by code in this repo:
 *   - "Quite a bit"                              plain rating, no text box used
 *   - { selected: "Very much", otherText: "…" }  live runner, text box used
 *   - { value: "…", otherValue|otherText: "…" }  the normalised shape the
 *                                                patient-overview model builds
 *                                                for FormDetailsPanel
 *
 * @returns {{ value: string|null, freeText: string|null }|null} null when the
 *   raw value is not a recognisable answer shape.
 */
function parseRawAnswer(raw) {
  if (typeof raw === 'string') return { value: raw, freeText: null };
  if (raw && typeof raw === 'object') {
    const value = typeof raw.selected === 'string'
      ? raw.selected
      : (typeof raw.value === 'string' ? raw.value : null);
    if (value === null) return null;
    const rawText = typeof raw.otherText === 'string'
      ? raw.otherText
      : (typeof raw.otherValue === 'string' ? raw.otherValue : '');
    const freeText = rawText.trim();
    return { value, freeText: freeText || null };
  }
  return null;
}

/**
 * Find the stored answer for a serviceUserVarName anywhere in an instance's
 * answers map, split into the rating and any free text typed beside it.
 *
 * Two answer-map shapes exist in production:
 *
 *   FLAT (live form runner — GenericFormPage.js):
 *     { "snapIV26_Informant_Q1": "Very much", ... }
 *     The variable name IS the top-level key.
 *
 *   NESTED (demo seeder — fill_form_answers):
 *     { "1": { "snapIV26_Informant_Q1": "Very much" }, ... }
 *     A question-number key wraps a map from variable name to answer.
 *
 * The flat shape is checked first (direct key lookup) because it is the
 * format every real patient has. The nested scan is the fallback for demo
 * data and any legacy records seeded before the runner switched to flat.
 *
 * @returns {{ value: string|null, freeText: string|null }}
 */
function findAnswer(answers, serviceUserVarName) {
  if (!answers || typeof answers !== 'object') return NO_ANSWER;

  // ── Flat shape: the variable name is a direct key in the answers map. ──
  const directHit = answers[serviceUserVarName];
  if (directHit !== undefined && directHit !== null) {
    const parsed = parseRawAnswer(directHit);
    if (parsed) return parsed;
  }

  // ── Nested shape: answers grouped by question number. ──
  for (const entry of Object.values(answers)) {
    if (!entry || typeof entry !== 'object') continue;
    // Skip entries that look like flat answer values rather than
    // question-number groups (they have 'selected'/'value' but not
    // the variable name we are looking for).
    const answer = entry[serviceUserVarName];
    if (answer === undefined || answer === null) continue;
    const parsed = parseRawAnswer(answer);
    if (parsed) return parsed;
  }
  return NO_ANSWER;
}

/**
 * Resolve one informant's SNAP-IV evidence for one DSM code.
 *
 * `freeText` is the optional example the informant typed under a high
 * rating. It is only returned when the stored rating is one the form
 * actually offered the text box for (the subquestion's `otherOptions`
 * keys) — the same rule the runner uses to decide whether to keep the
 * text — so text left behind by a since-lowered rating is never shown.
 *
 * @param {object|null} snapInstance - the informant's snapIV26Informant form
 *   instance document ({ formDefinition, answers, status, ... }), or null.
 * @param {string} dsmCode - e.g. 'A1a' .. 'A2i'.
 * @returns {{ value: string|null, freeText: string|null, questionText: string }|null}
 *   null when the instance has no item for the code (no SNAP assigned, or a
 *   non-ADHD code); value null when the item exists but is unanswered.
 */
export function resolveCypSnapEvidence(snapInstance, dsmCode) {
  const subsections = snapInstance?.formDefinition?.formSubsections;
  if (!subsections || !dsmCode) return null;

  for (const subsection of Object.values(subsections)) {
    for (const question of Object.values(subsection?.questions || {})) {
      const codes = question?.prefillsToProviderVarNames;
      if (!Array.isArray(codes) || !codes.includes(dsmCode)) continue;

      const subquestion = Object.values(question?.subquestions || {})[0];
      const varName = subquestion?.serviceUserVarName;
      const { value, freeText } = varName
        ? findAnswer(snapInstance.answers, varName)
        : NO_ANSWER;
      const textOffered = Boolean(
        value && subquestion?.otherOptions
        && Object.prototype.hasOwnProperty.call(subquestion.otherOptions, value),
      );
      return {
        value,
        freeText: textOffered ? freeText : null,
        questionText: question.questionTitle || '',
      };
    }
  }
  return null;
}

/**
 * Build the per-informant evidence sources for the SSI page.
 *
 * @param {object} informantsMap - patients/{id}.informants (uid -> map entry
 *   with informantRole, informantName, informantRelationshipToPatient,
 *   schoolName).
 * @param {object} informantFormInstances - uid -> { formKey -> instance }
 *   from ModularPatientDataContext.
 * @returns {Array<{ uid, role, label, scaleName, snapInstance }>} guardian
 *   first, then school rep — the order the CI's dual columns use.
 */
export function buildCypSnapEvidenceSources(informantsMap, informantFormInstances) {
  const sources = [];
  for (const [uid, entry] of Object.entries(informantsMap || {})) {
    const role = entry?.informantRole;
    if (role !== 'legalGuardian' && role !== 'schoolRep') continue;

    const relationship = (entry?.informantRelationshipToPatient || '').trim();
    const label = role === 'legalGuardian'
      ? `Parent/carer report${relationship ? ` (${relationship})` : ''}`
      : `School report${relationship ? ` (${relationship})` : ''}`;

    sources.push({
      uid,
      role,
      label,
      scaleName: CYP_SNAP_SCALE_NAME,
      snapInstance: informantFormInstances?.[uid]?.[CYP_SNAP_FORM_KEY] || null,
    });
  }
  // Guardian first, then school — the order every CYP dual-source surface uses.
  sources.sort((a, b) => (a.role === b.role ? 0 : a.role === 'legalGuardian' ? -1 : 1));
  return sources;
}
